// Branding por defecto antes de iniciar sesión.
// Una vez autenticado, el Dashboard ya pinta el branding del gym del usuario
// (gym.brand_name + logo). Estos valores se usan SOLO en pantallas previas a
// auth (login, registro, headers genéricos).
const fromEnv = (key, fallback) => {
  const v = (import.meta.env[key] || '').trim();
  return v || fallback;
};

// Nombre por defecto del producto. Una vez autenticado, el Dashboard pinta el
// brand del gym del usuario (white-label) y este valor solo se usa antes de
// auth o cuando el usuario no pertenece a ningún gym.
export const PUBLIC_BRAND_NAME = fromEnv('VITE_BRAND_NAME', 'ECOSISTEMA FIT');
export const PUBLIC_BRAND_TAGLINE = fromEnv('VITE_BRAND_TAGLINE', 'Nutrición, entrenamiento y comunidad en una sola cuenta.');
export const INSTITUTIONAL_SHELL_LABEL = PUBLIC_BRAND_NAME;

/** ID del entrenador asignado (tolera snake/camel y fallback desde coach_branding). */
export function getUserTrainerId(user) {
  if (!user) return null;
  return user.trainer_id ?? user.trainerId ?? user.coach_branding?.id ?? null;
}

/** Marca blanca activa del coach (cliente o el propio coach). */
export function isCoachWhiteLabelActive(user, coachBrand) {
  const coach = mergeCoachBrand(user, coachBrand);
  return coach?.white_label_enabled === true && Boolean(coach?.brand_name?.trim());
}

export function mergeCoachBrand(user, coachBrand) {
  if (coachBrand?.white_label_enabled) return coachBrand;
  if (user?.coach_branding?.white_label_enabled) return user.coach_branding;
  return coachBrand || user?.coach_branding || null;
}

function pickTrimmed(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text) return text;
  }
  return '';
}

/** Quita referencias visibles a la plataforma D28D en copy público (marca del gym). */
export function stripD28dFromPublicBrand(name) {
  return String(name || '')
    .replace(/\bd28d\b/gi, '')
    .replace(/\s+/g, ' ')
    .replace(/^[\s\-–|]+|[\s\-–|]+$/g, '')
    .trim();
}

export function getSanitizedPublicBrandName(name, fallback = PUBLIC_BRAND_NAME) {
  const cleaned = stripD28dFromPublicBrand(name);
  if (cleaned && !isD28dProductPlaceholder(cleaned)) return cleaned;
  const fallbackClean = stripD28dFromPublicBrand(fallback);
  if (fallbackClean && !isD28dProductPlaceholder(fallbackClean)) return fallbackClean;
  return 'ECOSISTEMA FIT';
}

/** Nombres genéricos de producto D28D — no sustituyen la marca del cliente. */
const D28D_PRODUCT_PLACEHOLDERS = new Set([
  'd28d',
  'virtual d28d',
  'virtual gym',
  'gym virtual',
  'gimnasio virtual',
  'gimnasio virtual d28d',
  'd28d gimnasio virtual',
  'metodo d28d',
  'método d28d',
  'tu gimnasio virtual',
  'ecosistema fit',
]);

export function isD28dProductPlaceholder(name) {
  const normalized = String(name || '').trim().toLowerCase();
  if (!normalized) return true;
  if (D28D_PRODUCT_PLACEHOLDERS.has(normalized)) return true;
  if (normalized === String(PUBLIC_BRAND_NAME).trim().toLowerCase()) return true;
  return /^gym qa\b/i.test(normalized);
}

/** Nombres de plantilla Training — no sustituyen la marca del coach en UI de gobernanza. */
export function isTrainingTemplatePlaceholder(name) {
  const normalized = String(name || '').trim().toLowerCase();
  if (!normalized) return false;
  if (normalized.includes('plantilla')) return true;
  if (normalized.includes('template')) return true;
  if (normalized.includes('nicolas') && normalized.includes('rio')) return true;
  if (normalized.includes('nicolás') && normalized.includes('rio')) return true;
  return false;
}

function isOrgNamePlaceholder(name) {
  return isD28dProductPlaceholder(name) || isTrainingTemplatePlaceholder(name);
}

/**
 * Jerarquía U3/U4: brand_name → organization_name → gym_name|trainer_name → plataforma.
 * La marca del cliente prevalece sobre nombres de programa, plantilla o contenido interno.
 */
export function resolveOrganizationBrandName({
  entity,
  tenantRow,
  platformName = PUBLIC_BRAND_NAME,
} = {}) {
  const brandName = pickTrimmed(entity?.brand_name, entity?.brandName);
  if (brandName) return brandName;

  const organizationName = pickTrimmed(
    entity?.organization_name,
    tenantRow?.tenant_name,
    tenantRow?.branding_config?.brand_name,
    tenantRow?.brandingConfig?.brand_name,
  );
  if (organizationName && !isOrgNamePlaceholder(organizationName)) {
    return organizationName;
  }

  const gymName = pickTrimmed(entity?.nombre);
  if (gymName && !isOrgNamePlaceholder(gymName)) return gymName;

  const tenantFallback = pickTrimmed(tenantRow?.tenant_code);
  if (tenantFallback && !isOrgNamePlaceholder(tenantFallback)) return tenantFallback;

  return pickTrimmed(platformName, PUBLIC_BRAND_NAME) || PUBLIC_BRAND_NAME;
}

export function resolveOrganizationBrandLogo({
  entity,
  tenantRow,
  platformLogo = '',
} = {}) {
  const logo = pickTrimmed(
    entity?.logo_url,
    entity?.logoUrl,
    tenantRow?.branding_config?.logo_url,
    tenantRow?.brandingConfig?.logo_url,
  );
  return logo || platformLogo || '';
}

export function resolveShellBrandName({ coachBrand, gymBrand, publicBrandName, user, tenantRow }) {
  if (user?.futbol_branding?.brand_name || user?.entry_host === 'futbol' || user?.module_access?.futbol) {
    const gymId = user?.gym_id ?? user?.gymId ?? null;
    const otherD28d = user?.module_access?.d28d || user?.module_access?.gym || user?.module_access?.live_classes;
    if (!otherD28d && (gymId == null || gymId === '')) {
      const schoolName = String(
        user?.futbol_branding?.brand_name
        || tenantRow?.tenant_name
        || user?.tenant_context?.tenant_name
        || '',
      ).trim();
      if (schoolName) return schoolName;
      return 'futbol.app';
    }
  }
  const coach = mergeCoachBrand(user, coachBrand);
  if (isCoachWhiteLabelActive(user, coach)) {
    const coachName = resolveOrganizationBrandName({
      entity: coach,
      platformName: publicBrandName,
    });
    if (coachName) return coachName;
  }
  if (gymBrand) {
    return resolveOrganizationBrandName({
      entity: gymBrand,
      tenantRow,
      platformName: publicBrandName,
    });
  }
  return publicBrandName || PUBLIC_BRAND_NAME;
}

function expandHexColor(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const raw = hex.replace('#', '').trim();
  if (raw.length === 3) return raw.split('').map((c) => c + c).join('');
  if (raw.length === 6) return raw;
  return null;
}

function relativeLuminance(hex) {
  const expanded = expandHexColor(hex);
  if (!expanded) return null;
  const rgb = [0, 2, 4].map((offset) => parseInt(expanded.slice(offset, offset + 2), 16) / 255);
  if (rgb.some((value) => Number.isNaN(value))) return null;
  const [r, g, b] = rgb.map((value) => (value <= 0.03928
    ? value / 12.92
    : ((value + 0.055) / 1.055) ** 2.4));
  return (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
}

function contrastRatioOnDark(hex, dark = '#0f0f0f') {
  const fg = relativeLuminance(hex);
  const bg = relativeLuminance(dark);
  if (fg == null || bg == null) return null;
  const lighter = Math.max(fg, bg);
  const darker = Math.min(fg, bg);
  return (lighter + 0.05) / (darker + 0.05);
}

function lightenHexTowardWhite(hex, amount = 0.15) {
  const raw = String(hex || '').replace('#', '').trim();
  if (raw.length !== 6 && raw.length !== 3) return hex;
  const expand = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  const r = parseInt(expand.slice(0, 2), 16);
  const g = parseInt(expand.slice(2, 4), 16);
  const b = parseInt(expand.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return hex;
  const mix = (c) => Math.min(255, Math.round(c + (255 - c) * amount));
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

/**
 * Acento legible sobre fondo oscuro del shell.
 * Preferir la marca (aclarar hasta contraste) en lugar de sustituir por amarillo fijo.
 */
function resolveReadableAccentOnDark(primary, fallback = '#ffd700') {
  if (!primary || typeof primary !== 'string') return fallback;
  let candidate = primary.trim();
  for (let i = 0; i < 10; i += 1) {
    const ratio = contrastRatioOnDark(candidate);
    if (ratio != null && ratio >= 4.5) return candidate;
    candidate = lightenHexTowardWhite(candidate, 0.14);
  }
  return candidate || fallback;
}

/** Texto legible sobre un color de marca (hex #RRGGBB). */
export function contrastTextForBg(hex) {
  if (!hex || typeof hex !== 'string') return '#000000';
  const h = hex.replace('#', '').trim();
  if (h.length !== 6 && h.length !== 3) return '#000000';
  const expand = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(expand.slice(0, 2), 16);
  const g = parseInt(expand.slice(2, 4), 16);
  const b = parseInt(expand.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return '#000000';
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#000000' : '#ffffff';
}

/** Colores de marca del gym en el shell (navbar, botones, acentos). */
export function applyGymShellColors(root, { primary, secondary } = {}) {
  if (primary) {
    const readableAccent = resolveReadableAccentOnDark(primary);
    root.style.setProperty('--shell-primary', primary);
    root.style.setProperty('--shell-accent-readable', readableAccent);
    root.style.setProperty('--d28d-yellow', readableAccent);
    root.style.setProperty('--d28d-yellow-hover', readableAccent);
    root.style.setProperty('--btn-primary-fg', contrastTextForBg(primary));
  } else {
    root.style.removeProperty('--shell-primary');
    root.style.removeProperty('--shell-accent-readable');
    root.style.removeProperty('--d28d-yellow');
    root.style.removeProperty('--d28d-yellow-hover');
    root.style.removeProperty('--btn-primary-fg');
  }
  if (secondary) {
    root.style.setProperty('--shell-secondary', secondary);
  } else {
    root.style.removeProperty('--shell-secondary');
  }
}

export function applyCoachShellTheme(coachBrand, { active = true } = {}) {
  const root = document.documentElement;
  if (!active || !coachBrand?.white_label_enabled) {
    root.removeAttribute('data-coach-wl');
    root.style.removeProperty('--shell-accent-readable');
    root.style.removeProperty('--btn-primary-fg');
    return;
  }
  root.setAttribute('data-coach-wl', 'true');
  const primary = coachBrand.primary_color || '#FFD700';
  const secondary = coachBrand.secondary_color || '#1a1a1a';
  const readableAccent = resolveReadableAccentOnDark(primary);
  root.style.setProperty('--shell-primary', primary);
  root.style.setProperty('--shell-accent-readable', readableAccent);
  root.style.setProperty('--d28d-yellow', readableAccent);
  root.style.setProperty('--d28d-yellow-hover', readableAccent);
  root.style.setProperty('--shell-secondary', secondary);
  root.style.setProperty('--btn-primary-fg', contrastTextForBg(primary));
}

export function resolveShellBrandLogo({ coachBrand, gymBrand, publicLogo, user, tenantRow }) {
  const coach = mergeCoachBrand(user, coachBrand);
  if (isCoachWhiteLabelActive(user, coach) && coach?.logo_url) return coach.logo_url;
  if (gymBrand) {
    return resolveOrganizationBrandLogo({
      entity: gymBrand,
      tenantRow,
      platformLogo: publicLogo,
    });
  }
  return publicLogo || '';
}

/**
 * Evita duplicar marca en navbar: logo + texto "D28D" repetido.
 * Con logo: solo texto complementario (ej. "Marca Blanca") o nada si el nombre es genérico D28D.
 */
export function resolveNavbarBrandDisplay({
  brandName,
  brandLogoSrc,
  coachWhiteLabelActive = false,
} = {}) {
  const name = String(brandName || PUBLIC_BRAND_NAME).trim();
  const hasLogo = Boolean(String(brandLogoSrc || '').trim());
  const parts = name.split(/\s+/).filter(Boolean);
  const head = parts[0] || 'ECOSISTEMA';
  const tail = parts.slice(1).join(' ');

  if (!hasLogo) {
    return { showLogo: false, showText: true, head, tail, fullName: name };
  }

  if (coachWhiteLabelActive) {
    return { showLogo: true, showText: true, head, tail, fullName: name, textOnlyTail: false };
  }

  if (!tail || isD28dProductPlaceholder(name)) {
    return { showLogo: true, showText: false, head, tail: '', fullName: name };
  }

  return { showLogo: true, showText: true, head, tail, fullName: name, textOnlyTail: true };
}
